import type { SfxKey } from '@/audio/AudioManager';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import {
  COMBO_SHIELD,
  GOLD_CONSTANTS,
  SCORE_BOOST,
  getScoreBoostMultiplier,
} from '@/economy/upgrades';
import { BONUS_SYSTEM } from '@/game/constants';
import { formatCooldown } from '@/lib/format';
import { useEffect, useState } from 'react';

interface PurchaseFx {
  id: number;
  text: string;
}

const NO_FX: PurchaseFx = { id: 0, text: '' };

const TOKEN_COOLDOWN_MS = BONUS_SYSTEM.AD_COOLDOWN_MS;
const TOKEN_CAP = BONUS_SYSTEM.MAX_TOKENS;
const GOLD_COOLDOWN_MS = GOLD_CONSTANTS.AD_COOLDOWN_MINUTES * 60 * 1000;
const GOLD_PER_AD = GOLD_CONSTANTS.GOLD_PER_AD;

interface UpgradeShopModalProps {
  open: boolean;
  onClose: () => void;
  ready: boolean;
  gold: number;
  tokens: number;
  scoreBoostLevel: number;
  comboShields: number;
  lastTokenAdAt: number;
  lastGoldAdAt: number;
  tokenCooldownMs?: number;
  goldCooldownMs?: number;
  onPurchaseScoreBoost: () => { success: boolean; error?: string };
  onPurchaseShields: (bulk: boolean) => { success: boolean; error?: string };
  onWatchAdForTokens: () => Promise<{ shown: boolean; rewarded?: boolean }>;
  onWatchAdForGold: () => Promise<{ shown: boolean; rewarded?: boolean }>;
  onSfx?: (key: SfxKey) => void;
  adsEnabled?: boolean;
}

export function UpgradeShopModal({
  open,
  onClose,
  ready,
  gold,
  tokens,
  scoreBoostLevel,
  comboShields,
  lastTokenAdAt,
  lastGoldAdAt,
  tokenCooldownMs = TOKEN_COOLDOWN_MS,
  goldCooldownMs = GOLD_COOLDOWN_MS,
  onPurchaseScoreBoost,
  onPurchaseShields,
  onWatchAdForTokens,
  onWatchAdForGold,
  onSfx,
  adsEnabled = true,
}: UpgradeShopModalProps) {
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [tokenAdStatus, setTokenAdStatus] = useState<'idle' | 'pending' | 'failed'>('idle');
  const [goldAdStatus, setGoldAdStatus] = useState<'idle' | 'pending' | 'failed'>('idle');
  const [earnFx, setEarnFx] = useState<PurchaseFx>(NO_FX);
  const [boostFx, setBoostFx] = useState<PurchaseFx>(NO_FX);
  const [shieldFx, setShieldFx] = useState<PurchaseFx>(NO_FX);

  const triggerFx = (
    setter: React.Dispatch<React.SetStateAction<PurchaseFx>>,
    text: string,
    button: HTMLElement | null,
  ): void => {
    setter((prev) => ({ id: prev.id + 1, text }));
    onSfx?.('reward-claim');
    if (button) {
      button.classList.remove('title-btn--flash');
      void button.offsetWidth;
      button.classList.add('title-btn--flash');
      window.setTimeout(() => button.classList.remove('title-btn--flash'), 700);
    }
  };

  const tokenCooldownLeftMs = Math.max(0, lastTokenAdAt + tokenCooldownMs - now);
  const tokenOnCooldown = tokenCooldownLeftMs > 0;
  const tokensCapped = tokens >= TOKEN_CAP;
  const tokenAdDisabled = !ready || tokenOnCooldown || tokensCapped || tokenAdStatus === 'pending';

  const goldCooldownLeftMs = Math.max(0, lastGoldAdAt + goldCooldownMs - now);
  const goldOnCooldown = goldCooldownLeftMs > 0;
  const goldAdDisabled = !ready || goldOnCooldown || goldAdStatus === 'pending';

  const anyOnCooldown = tokenOnCooldown || goldOnCooldown;

  useEffect(() => {
    if (!open || !anyOnCooldown) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [open, anyOnCooldown]);

  useEffect(() => {
    if (open) {
      setNow(Date.now());
      return;
    }
    // Reset transient fx so a prior purchase's pill doesn't replay when the
    // dialog re-opens (display:none → block restarts CSS animations).
    setEarnFx(NO_FX);
    setBoostFx(NO_FX);
    setShieldFx(NO_FX);
  }, [open]);

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const atMaxBoost = scoreBoostLevel >= SCORE_BOOST.maxLevel;
  const nextBoostCost = atMaxBoost ? -1 : (SCORE_BOOST.costs[scoreBoostLevel] as number);
  const currentBoostPct = Math.round(getScoreBoostMultiplier(scoreBoostLevel) * 100);
  const nextBoostPct = atMaxBoost
    ? currentBoostPct
    : Math.round(getScoreBoostMultiplier(scoreBoostLevel + 1) * 100);
  const boostDisabled = atMaxBoost || gold < nextBoostCost;

  const canBuyOne =
    comboShields + 1 <= COMBO_SHIELD.maxShields && gold >= COMBO_SHIELD.costPerShield;
  const canBuyBulk =
    comboShields + COMBO_SHIELD.bulkAmount <= COMBO_SHIELD.maxShields &&
    gold >= COMBO_SHIELD.bulkCost;

  const handleBoost = (event: React.MouseEvent<HTMLButtonElement>) => {
    setError(null);
    const button = event.currentTarget;
    const result = onPurchaseScoreBoost();
    if (result.success) {
      triggerFx(setBoostFx, `Lv ${scoreBoostLevel + 1} → +${nextBoostPct}%`, button);
    } else if (result.error) {
      setError(result.error);
    }
  };

  const handleShield = (bulk: boolean, event: React.MouseEvent<HTMLButtonElement>) => {
    setError(null);
    const button = event.currentTarget;
    const result = onPurchaseShields(bulk);
    if (result.success) {
      const amount = bulk ? COMBO_SHIELD.bulkAmount : 1;
      triggerFx(setShieldFx, `+${amount} 🛡️`, button);
    } else if (result.error) {
      setError(result.error);
    }
  };

  const handleTokenAd = async (event: React.MouseEvent<HTMLButtonElement>) => {
    const button = event.currentTarget;
    setTokenAdStatus('pending');
    try {
      const result = await onWatchAdForTokens();
      const ok = result.shown && result.rewarded;
      setTokenAdStatus(ok ? 'idle' : 'failed');
      if (ok) triggerFx(setEarnFx, '+3 ⭐', button);
    } catch {
      setTokenAdStatus('failed');
    }
  };

  const handleGoldAd = async (event: React.MouseEvent<HTMLButtonElement>) => {
    const button = event.currentTarget;
    setGoldAdStatus('pending');
    try {
      const result = await onWatchAdForGold();
      const ok = result.shown && result.rewarded;
      setGoldAdStatus(ok ? 'idle' : 'failed');
      if (ok) triggerFx(setEarnFx, `+${GOLD_PER_AD} 🪙`, button);
    } catch {
      setGoldAdStatus('failed');
    }
  };

  const shieldsAtCap = comboShields >= COMBO_SHIELD.maxShields;
  const shieldHint = shieldsAtCap
    ? 'Inventory full.'
    : !canBuyOne
      ? `Need ${COMBO_SHIELD.costPerShield - gold}🪙 more.`
      : null;
  const boostHint = atMaxBoost
    ? null
    : gold < nextBoostCost
      ? `Need ${nextBoostCost - gold}🪙 more.`
      : null;

  return (
    <Modal open={open} onClose={handleClose} title="Upgrade Shop">
      <div className="shop-balance" aria-live="polite">
        <span className="shop-balance__label">Wallet</span>
        <span className="shop-balance__chips">
          <span className="chip chip--gold" aria-label={`${gold} gold`}>
            <span className="coin-icon" aria-hidden="true" />
            <span>{gold}</span>
          </span>
          {adsEnabled && (
            <span className="chip chip--token" aria-label={`${tokens} bonus tokens`}>
              <span aria-hidden="true">⭐</span>
              <span>
                {tokens}/{TOKEN_CAP}
              </span>
            </span>
          )}
        </span>
      </div>

      {adsEnabled && (
        <>
          <h4 className="shop-category">Earn</h4>
          <section className="shop-section" aria-label="Earn currency">
            <header className="shop-section__header">
              <span aria-hidden="true" className="shop-section__icon">
                📺
              </span>
              <div className="shop-section__title">
                <h3>Watch ads</h3>
              </div>
            </header>
            <p className="shop-desc">
              Earn currency from short rewarded ads. Tokens (⭐) grant a 1.5× score multiplier on a
              single retry. Gold (<span className="coin-icon" aria-hidden="true" />) buys permanent
              upgrades below.
            </p>
            <div className="shop-buttons">
              <button
                type="button"
                className="title-btn title-btn--reward title-ad-tokens"
                onClick={handleTokenAd}
                disabled={tokenAdDisabled}
                aria-describedby="shop-ad-hint"
              >
                {tokenAdStatus === 'pending' ? (
                  <>
                    <Spinner />
                    Loading ad…
                  </>
                ) : tokensCapped ? (
                  'Tokens full'
                ) : tokenOnCooldown ? (
                  `+3 tokens (${formatCooldown(tokenCooldownLeftMs)})`
                ) : (
                  '+3 tokens (watch ad)'
                )}
              </button>
              <button
                type="button"
                className="title-btn title-btn--reward title-ad-gold"
                onClick={handleGoldAd}
                disabled={goldAdDisabled}
                aria-describedby="shop-ad-hint"
              >
                {goldAdStatus === 'pending' ? (
                  <>
                    <Spinner />
                    Loading ad…
                  </>
                ) : goldOnCooldown ? (
                  `+${GOLD_PER_AD} gold (${formatCooldown(goldCooldownLeftMs)})`
                ) : (
                  `+${GOLD_PER_AD} gold (watch ad)`
                )}
              </button>
            </div>
            {(tokenAdStatus === 'failed' || goldAdStatus === 'failed') && (
              <p id="shop-ad-hint" className="shop-error" role="alert">
                Ad not available — try again later.
              </p>
            )}
            {earnFx.id > 0 && (
              <span key={earnFx.id} className="shop-fx" aria-hidden="true">
                {earnFx.text}
              </span>
            )}
          </section>
        </>
      )}

      <h4 className="shop-category">Spend</h4>
      <section className="shop-section" aria-label="Score Boost">
        <header className="shop-section__header">
          <span aria-hidden="true" className="shop-section__icon">
            🏆
          </span>
          <div className="shop-section__title">
            <h3>Score Boost</h3>
            <span className="shop-level">
              Lv {scoreBoostLevel}/{SCORE_BOOST.maxLevel}
            </span>
          </div>
        </header>
        <p className="shop-desc">{SCORE_BOOST.description}.</p>
        <p className="shop-effect">
          Current: <strong>+{currentBoostPct}%</strong>
          {!atMaxBoost && (
            <>
              {' → '}Next: <strong>+{nextBoostPct}%</strong>
            </>
          )}
        </p>
        <button type="button" className="title-btn" onClick={handleBoost} disabled={boostDisabled}>
          {atMaxBoost ? (
            'Maxed out'
          ) : (
            <>
              Upgrade ({nextBoostCost}
              <span className="coin-icon" aria-hidden="true" />)
            </>
          )}
        </button>
        {boostHint && <p className="shop-hint">{boostHint}</p>}
        {boostFx.id > 0 && (
          <span key={boostFx.id} className="shop-fx" aria-hidden="true">
            {boostFx.text}
          </span>
        )}
      </section>

      <section className="shop-section" aria-label="Combo Shield">
        <header className="shop-section__header">
          <span aria-hidden="true" className="shop-section__icon">
            🛡️
          </span>
          <div className="shop-section__title">
            <h3>Combo Shield</h3>
            <span className="shop-level">
              Owned {comboShields}/{COMBO_SHIELD.maxShields}
            </span>
          </div>
        </header>
        <p className="shop-desc">
          Protect your combo from one wrong tap. Up to {COMBO_SHIELD.maxPerGame} per game.
        </p>
        <div className="shop-buttons">
          <button
            type="button"
            className="title-btn"
            onClick={(e) => handleShield(false, e)}
            disabled={!canBuyOne}
          >
            Buy 1 ({COMBO_SHIELD.costPerShield}
            <span className="coin-icon" aria-hidden="true" />)
          </button>
          <button
            type="button"
            className="title-btn"
            onClick={(e) => handleShield(true, e)}
            disabled={!canBuyBulk}
          >
            Buy {COMBO_SHIELD.bulkAmount} ({COMBO_SHIELD.bulkCost}
            <span className="coin-icon" aria-hidden="true" />)
          </button>
        </div>
        {shieldHint && <p className="shop-hint">{shieldHint}</p>}
        {shieldFx.id > 0 && (
          <span key={shieldFx.id} className="shop-fx" aria-hidden="true">
            {shieldFx.text}
          </span>
        )}
      </section>

      {error && (
        <p className="shop-error" role="alert">
          {error}
        </p>
      )}
    </Modal>
  );
}
